import * as Sentry from "@sentry/react";
import React from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { environment } from "scripts/application/setting";
import { SENTRY_DSN } from "utils/constants";
import { shouldDropSentryEvent } from "scripts/logging/sentry-filters";

export interface IMessageOptions {
  tags: Record<string, string | number | undefined>;
}

const enabled = true;
if (
  enabled &&
  environment !== "local" &&
  SENTRY_DSN &&
  typeof SENTRY_DSN === "string" &&
  SENTRY_DSN?.trim() !== ""
) {
  configureSentry();
}
function configureSentry() {
  Sentry.init({
    environment,
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/aimode\.har\.com/],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Drop noise from browser extensions / injected third-party scripts that
    // dispatch a *synthetic* `unhandledrejection` CustomEvent on window
    // (isTrusted: false, no `.reason`). Sentry's global handler can't extract a
    // reason and reports it as "…captured as promise rejection with keys:
    // [object has no keys]". These are not real app errors.
    ignoreErrors: [
      /captured as promise rejection with keys: \[object has no keys\]/,
    ],

    // See `sentry-filters` for what gets dropped and why.
    beforeSend(event, hint) {
      return shouldDropSentryEvent(event, hint) ? null : event;
    },
  });
}

export function login(username: string, email: string) {
  Sentry.setUser({ email, username });
}

export function logout() {
  Sentry.setUser(null);
}

export function captureException(ex: any, options?: IMessageOptions): string {
  return Sentry.captureException(ex, options);
}

export function captureMessage(
  message: string,
  options?: IMessageOptions
): string {
  return Sentry.captureMessage(message, options);
}
